import axios, {
  type AxiosError,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import {
  getAccessToken,
  getAuthCookieDomain,
  getRefreshToken,
  writeAuthTokens,
} from '@/shared/session/auth';
import { logger } from '@/shared/libs/logger';
import logout from './logout';
import { externalConnections } from '@/constants/Constants';

type FailedRequest = {
  reject: (reason?: unknown) => void;
  resolve: (value: unknown) => void;
};

let failedQueue: FailedRequest[] = [];
let isRefreshing = false;

const processQueue = (error: unknown) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(undefined);
    }
  });

  failedQueue = [];
};

export function createAxiosClient({ options }: { options: AxiosRequestConfig }) {
  const client = axios.create(options);
  client.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      config.headers.Authorization = `Bearer ${getAccessToken()}`;
      return config;
    },
    (error) => {
      return Promise.reject(error);
    },
  );

  client.interceptors.response.use(
    // Any status code that lie within the range of 2xx cause this function to trigger
    (response) => {
      return response;
    },
    // Existing refresh behavior is intentionally preserved during the architecture migration.
    // eslint-disable-next-line complexity
    async (error: AxiosError<{ message?: string }>) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
      // In "axios": "^1.1.3" there is an issue with headers, and this is the workaround.
      // originalRequest.headers = JSON.parse(JSON.stringify(originalRequest.headers || {}));
      const refreshToken = getRefreshToken();
      // If error, process all the requests in the queue and logout the user.
      const handleError = (refreshError: unknown) => {
        processQueue(refreshError);
        logout({ reason: 'auth_error' });
        return Promise.reject(refreshError);
      };

      // Refresh token conditions
      if (
        refreshToken &&
        error.response?.status === 401 &&
        error.response.data.message === 'Unauthorized' &&
        originalRequest?.url !== externalConnections.refreshTokenUrl &&
        originalRequest?._retry !== true
      ) {
        if (isRefreshing) {
          try {
            await new Promise(function (resolve, reject) {
              failedQueue.push({ resolve, reject });
            });
            return await client(originalRequest);
          } catch (err) {
            return await Promise.reject(err);
          }
        }
        isRefreshing = true;
        originalRequest._retry = true;
        return await axios({
          url: externalConnections.refreshTokenUrl,
          data: { refresh_token: refreshToken },
          method: 'POST',
        })
          .then((res) => {
            // One write, so a reload can never restore a mismatched pair. A
            // rejected cookie leaves the stale token in place, so the retry
            // below 401s into a logout — record why before that happens.
            const rotated = {
              access_token: res.data?.access_token,
              refresh_token: res.data?.refresh_token,
            };
            if (!writeAuthTokens(rotated)) {
              logger.error('Auth cookie not stored on token refresh', {
                cookieDomain: getAuthCookieDomain(),
                // Separates a malformed refresh response from a browser
                // rejection, without logging the tokens themselves.
                responseHadTokens: Boolean(rotated.access_token && rotated.refresh_token),
              });
            }
            processQueue(null);

            return client(originalRequest);
          }, handleError)
          .finally(() => {
            isRefreshing = false;
          });
      }

      // Refresh token missing or expired => logout user...
      if (
        error.response?.status === 401 ||
        error.response?.data?.message === 'Unauthorized' ||
        error.response?.status === 403
      ) {
        return handleError(error);
      }

      // Any status codes that falls outside the range of 2xx cause this function to trigger
      // Do something with response error
      throw error;
    },
  );

  return client;
}
